import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { 
  Search, Building2, Download, Package, AlertTriangle, X, ArrowUpDown, ArrowUp, ArrowDown,
  ShoppingCart, Plus, Truck, Clock, Tag, Factory
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingOverlay from '../components/LoadingOverlay';
import ErrorState from '../components/ErrorState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to format date as relative time or short date
const formatOrderDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const InventoryHQ = () => {
  const { getAuthHeaders, user } = useAuth();
  const [data, setData] = useState({ depots: [], items: [], categories: [] });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Check if user is depot (hide actions for depot users)
  const isDepot = user?.role === 'depot';
  
  // Production data toggle
  const [showProductionQty, setShowProductionQty] = useState(false);
  const [productionData, setProductionData] = useState({ items: {} });
  
  // Filter and sort state
  const [statusFilter, setStatusFilter] = useState('all');
  const [depotFilter, setDepotFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Order state
  const [activeOrders, setActiveOrders] = useState({});
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showContributeDialog, setShowContributeDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [orderQuantity, setOrderQuantity] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [inventoryRes, ordersRes, productionRes] = await Promise.all([
        axios.get(`${API}/inventory/hq`, { headers: getAuthHeaders() }),
        axios.get(`${API}/orders/active/summary`, { headers: getAuthHeaders() }),
        axios.get(`${API}/production/items-for-inventory`, { headers: getAuthHeaders() }).catch(() => ({ data: { items: {} } }))
      ]);
      setData(inventoryRes.data);
      setActiveOrders(ordersRes.data);
      setProductionData(productionRes.data);
    } catch (error) {
      console.error('Error fetching inventory HQ data:', error);
      setError('Unable to load inventory data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [getAuthHeaders]);

  // Filtered and sorted data
  const filteredItems = useMemo(() => {
    let items = [...data.items];
    
    // Apply search filter - search by item_name only (SKU may have duplicates)
    if (search.trim()) {
      const query = search.toLowerCase();
      items = items.filter(item => 
        (item.item_name || '').toLowerCase().includes(query)
      );
    }
    
    // Apply status filter
    if (statusFilter === 'reorder') {
      items = items.filter(item => item.reorder_required);
    } else if (statusFilter === 'ok') {
      items = items.filter(item => !item.reorder_required);
    } else if (statusFilter === 'on_order') {
      items = items.filter(item => activeOrders[item.item_name]);
    }
    
    // Apply depot filter (items that have stock in selected depot)
    if (depotFilter !== 'all') {
      items = items.filter(item => item.depots[depotFilter] && item.depots[depotFilter] > 0);
    }
    
    // Apply category filter
    if (categoryFilter !== 'all') {
      if (categoryFilter === 'uncategorized') {
        items = items.filter(item => !item.category_id);
      } else {
        items = items.filter(item => item.category_id === categoryFilter);
      }
    }
    
    // Apply sorting
    if (sortConfig.key) {
      items.sort((a, b) => {
        let aVal, bVal;
        
        if (sortConfig.key === 'sku' || sortConfig.key === 'item_name') {
          aVal = a[sortConfig.key].toLowerCase();
          bVal = b[sortConfig.key].toLowerCase();
        } else if (sortConfig.key.startsWith('depot_')) {
          const depotName = sortConfig.key.replace('depot_', '');
          aVal = a.depots[depotName] || 0;
          bVal = b.depots[depotName] || 0;
        } else {
          aVal = a[sortConfig.key];
          bVal = b[sortConfig.key];
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return items;
  }, [data.items, search, statusFilter, depotFilter, categoryFilter, sortConfig, activeOrders]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" />
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setDepotFilter('all');
    setCategoryFilter('all');
    setSortConfig({ key: null, direction: 'asc' });
  };

  const hasActiveFilters = search || statusFilter !== 'all' || depotFilter !== 'all' || categoryFilter !== 'all';

  const exportToExcel = () => {
    if (filteredItems.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const baseHeaders = ['SKU', 'Item Name', ...data.depots, 'Total Stock', 'Buffer Level', 'Avg Sale'];
    const productionHeaders = showProductionQty ? ['Prog Qty', 'Dispatched', 'Pending'] : [];
    const endHeaders = ['Status', 'Reorder Qty', 'On Order'];
    const headers = [...baseHeaders, ...productionHeaders, ...endHeaders];
    
    const rows = filteredItems.map(item => {
      const prodInfo = productionData.items[item.item_name] || { program_qty: 0, dispatched_qty: 0 };
      const pendingQty = Math.max(0, prodInfo.program_qty - prodInfo.dispatched_qty);
      
      const baseData = [
        item.sku,
        item.item_name,
        ...data.depots.map(depot => item.depots[depot] || 0),
        item.total_stock,
        item.buffer_level,
        item.avg_sale || 0
      ];
      const productionRowData = showProductionQty ? [prodInfo.program_qty, prodInfo.dispatched_qty, pendingQty] : [];
      const endData = [
        item.reorder_required ? 'Reorder Required' : 'OK',
        item.reorder_required ? item.reorder_qty : 0,
        activeOrders[item.item_name]?.total_ordered_qty || 0
      ];
      
      return [...baseData, ...productionRowData, ...endData];
    });
    
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    
    // Set column widths
    const baseWidths = [
      { wch: 18 },  // SKU
      { wch: 35 },  // Item Name
      ...data.depots.map(() => ({ wch: 12 })),  // Depot columns
      { wch: 12 },  // Total Stock
      { wch: 12 },  // Buffer
      { wch: 12 }   // Avg Sale
    ];
    const productionWidths = showProductionQty ? [{ wch: 10 }, { wch: 10 }, { wch: 10 }] : [];
    const endWidths = [
      { wch: 16 },  // Status
      { wch: 12 },  // Reorder Qty
      { wch: 12 }   // On Order
    ];
    ws['!cols'] = [...baseWidths, ...productionWidths, ...endWidths];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory HQ');
    
    // Generate filename with filter info
    let filename = `inventory_hq_${new Date().toISOString().split('T')[0]}`;
    if (search) filename += `_search-${search.substring(0, 20)}`;
    if (statusFilter !== 'all') filename += `_${statusFilter}`;
    if (depotFilter !== 'all') filename += `_${depotFilter}`;
    if (showProductionQty) filename += `_with-production`;
    filename += '.xlsx';
    
    XLSX.writeFile(wb, filename);
    toast.success(`Exported ${filteredItems.length} items to Excel`);
  };

  // Order handling
  const openCreateOrder = (item) => {
    setSelectedItem(item);
    setOrderQuantity(item.reorder_qty?.toString() || '');
    setOrderNotes('');
    setShowOrderDialog(true);
  };

  const openContributeDialog = (item) => {
    setSelectedItem(item);
    const orderInfo = activeOrders[item.item_name];
    if (orderInfo && orderInfo.order_ids.length > 0) {
      setSelectedOrderId(orderInfo.order_ids[0]); // Use first active order
    }
    setOrderQuantity('');
    setOrderNotes('');
    setShowContributeDialog(true);
  };

  const handleCreateOrder = async () => {
    if (!orderQuantity || parseInt(orderQuantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/orders`,
        {
          item_name: selectedItem.item_name,
          sku: selectedItem.sku,
          quantity: parseInt(orderQuantity),
          notes: orderNotes || null
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`Reorder created for ${selectedItem.item_name}`);
      setShowOrderDialog(false);
      resetOrderForm();
      fetchData(); // Refresh to show new order
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContribute = async () => {
    if (!orderQuantity || parseInt(orderQuantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/orders/${selectedOrderId}/contribute`,
        {
          quantity: parseInt(orderQuantity),
          notes: orderNotes || null
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`Added ${orderQuantity} units to existing order`);
      setShowContributeDialog(false);
      resetOrderForm();
      fetchData(); // Refresh to show updated order
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to order');
    } finally {
      setSubmitting(false);
    }
  };

  const resetOrderForm = () => {
    setSelectedItem(null);
    setOrderQuantity('');
    setOrderNotes('');
    setSelectedOrderId(null);
  };

  const totalStockAcrossAll = filteredItems.reduce((sum, item) => sum + item.total_stock, 0);
  const itemsNeedingReorder = filteredItems.filter(item => item.reorder_required).length;
  const itemsOnOrder = Object.keys(activeOrders).length;

  // Show loading overlay
  if (loading) {
    return <LoadingOverlay message="Loading inventory data..." />;
  }

  // Show error state
  if (error) {
    return <ErrorState 
      title="Failed to load inventory" 
      message={error} 
      onRetry={fetchData}
    />;
  }

  return (
    <div className="space-y-6" data-testid="inventory-hq-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Inventory HQ
          </h1>
          <p className="text-slate-500 mt-1">Consolidated stock view across all depot locations</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Production Qty Toggle */}
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border">
            <Factory className="w-4 h-4 text-blue-600" />
            <Label htmlFor="production-toggle" className="text-sm font-medium cursor-pointer">
              Production Qty
            </Label>
            <Switch
              id="production-toggle"
              checked={showProductionQty}
              onCheckedChange={setShowProductionQty}
              data-testid="production-toggle"
            />
          </div>
          <Button onClick={exportToExcel} variant="outline" data-testid="export-hq-btn">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Depots</p>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">{data.depots.length}</p>
              </div>
              <div className="bg-blue-100 p-2 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Unique Items</p>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">
                  {filteredItems.length}
                  {hasActiveFilters && <span className="text-sm font-normal text-slate-400"> of {data.items.length}</span>}
                </p>
              </div>
              <div className="bg-green-100 p-2 rounded-lg">
                <Package className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Stock</p>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">{totalStockAcrossAll.toLocaleString()}</p>
              </div>
              <div className="bg-purple-100 p-2 rounded-lg">
                <Package className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Need Reorder</p>
                <p className="text-2xl font-bold text-red-600 tabular-nums">{itemsNeedingReorder}</p>
              </div>
              <div className="bg-red-100 p-2 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">On Order</p>
                <p className="text-2xl font-bold text-cyan-600 tabular-nums">{itemsOnOrder}</p>
              </div>
              <div className="bg-cyan-100 p-2 rounded-lg">
                <Truck className="w-5 h-5 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inventory HQ Table */}
      <Card data-testid="inventory-hq-table">
        <CardHeader className="border-b">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Building2 className="w-5 h-5" />
                {depotFilter !== 'all' ? `Stock at ${depotFilter}` : 'Stock Across Locations'}
                <Badge variant="secondary" className="ml-2">{filteredItems.length} of {data.items.length} items</Badge>
              </CardTitle>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 hover:text-slate-700">
                  <X className="w-4 h-4 mr-1" />
                  Clear Filters
                </Button>
              )}
            </div>
            
            {/* Search and Filters Row */}
            <div className="flex flex-col md:flex-row gap-3">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by SKU or Item Name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="hq-search"
                />
              </div>
              
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-[180px]" data-testid="status-filter">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Stock Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="reorder">
                    <span className="flex items-center gap-2 text-red-600">Reorder Required</span>
                  </SelectItem>
                  <SelectItem value="ok">
                    <span className="flex items-center gap-2 text-green-600">Stock OK</span>
                  </SelectItem>
                  <SelectItem value="on_order">
                    <span className="flex items-center gap-2 text-cyan-600">On Order</span>
                  </SelectItem>
                </SelectContent>
              </Select>
              
              {/* Depot Filter */}
              <Select value={depotFilter} onValueChange={setDepotFilter}>
                <SelectTrigger className="w-full md:w-[180px]" data-testid="depot-filter">
                  <Building2 className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by Depot" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Depots</SelectItem>
                  {data.depots.map(depot => (
                    <SelectItem key={depot} value={depot}>{depot}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Category Filter */}
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full md:w-[180px]" data-testid="category-filter">
                  <Tag className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="uncategorized">
                    <span className="text-slate-400">Uncategorized</span>
                  </SelectItem>
                  {data.categories?.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              {hasActiveFilters ? (
                <>
                  <p className="text-slate-500 text-lg">No items match your filters</p>
                  <Button variant="link" onClick={clearFilters} className="mt-2">
                    Clear all filters
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-slate-500 text-lg">No inventory items found</p>
                  <p className="text-slate-400 text-sm">Upload stock data to see consolidated inventory</p>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th 
                      className="sticky left-0 bg-slate-50 z-20 cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('sku')}
                    >
                      <span className="flex items-center">
                        SKU {getSortIcon('sku')}
                      </span>
                    </th>
                    <th 
                      className="sticky left-[120px] bg-slate-50 z-20 cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('item_name')}
                    >
                      <span className="flex items-center">
                        Item Name {getSortIcon('item_name')}
                      </span>
                    </th>
                    {depotFilter === 'all' ? (
                      // Show all depot columns when no depot is selected
                      data.depots.map(depot => (
                        <th 
                          key={depot} 
                          className="text-right min-w-[100px] cursor-pointer hover:bg-slate-100 select-none"
                          onClick={() => handleSort(`depot_${depot}`)}
                        >
                          <div className="flex items-center justify-end gap-1">
                            <Building2 className="w-3 h-3" />
                            {depot}
                            {getSortIcon(`depot_${depot}`)}
                          </div>
                        </th>
                      ))
                    ) : (
                      // Show only selected depot column
                      <th 
                        className="text-right min-w-[120px] cursor-pointer hover:bg-slate-100 select-none bg-blue-50"
                        onClick={() => handleSort(`depot_${depotFilter}`)}
                      >
                        <div className="flex items-center justify-end gap-1">
                          <Building2 className="w-3 h-3 text-blue-600" />
                          <span className="text-blue-700">{depotFilter} Stock</span>
                          {getSortIcon(`depot_${depotFilter}`)}
                        </div>
                      </th>
                    )}
                    <th 
                      className="text-right bg-slate-100 font-bold cursor-pointer hover:bg-slate-200 select-none"
                      onClick={() => handleSort('total_stock')}
                    >
                      <span className="flex items-center justify-end">
                        Total Stock {getSortIcon('total_stock')}
                      </span>
                    </th>
                    <th 
                      className="text-right cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('buffer_level')}
                    >
                      <span className="flex items-center justify-end">
                        Buffer {getSortIcon('buffer_level')}
                      </span>
                    </th>
                    <th 
                      className="text-right cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('avg_sale')}
                    >
                      <span className="flex items-center justify-end">
                        Avg Sale {getSortIcon('avg_sale')}
                      </span>
                    </th>
                    {/* Production Qty columns - conditionally rendered */}
                    {showProductionQty && (
                      <>
                        <th className="text-right bg-blue-50 text-blue-700 min-w-[90px]">
                          <div className="flex items-center justify-end gap-1">
                            <Factory className="w-3 h-3" />
                            Prog Qty
                          </div>
                        </th>
                        <th className="text-right bg-green-50 text-green-700 min-w-[90px]">
                          <div className="flex items-center justify-end gap-1">
                            <Truck className="w-3 h-3" />
                            Dispatched
                          </div>
                        </th>
                        <th className="text-right bg-orange-50 text-orange-700 min-w-[80px]">
                          Pending
                        </th>
                      </>
                    )}
                    <th>Status</th>
                    <th 
                      className="text-right cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('reorder_qty')}
                    >
                      <span className="flex items-center justify-end">
                        Reorder Qty {getSortIcon('reorder_qty')}
                      </span>
                    </th>
                    {!isDepot && <th className="text-center">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => {
                    const orderInfo = activeOrders[item.item_name];
                    const hasActiveOrder = !!orderInfo;
                    
                    return (
                      <tr 
                        key={item.item_name} 
                        className={item.reorder_required ? 'reorder-alert' : ''}
                        data-testid={`hq-row-${item.item_name}`}
                      >
                        <td className="font-mono text-sm font-medium sticky left-0 bg-white z-10">{item.sku}</td>
                        <td className="font-medium sticky left-[120px] bg-white z-10">
                          <div className="flex items-center gap-2">
                            {item.item_name}
                            {hasActiveOrder && (
                              <Badge 
                                className="bg-cyan-100 text-cyan-700 border-0 text-xs"
                                data-testid={`order-badge-${item.item_name}`}
                              >
                                <Truck className="w-3 h-3 mr-1" />
                                {orderInfo.total_ordered_qty.toLocaleString()} ordered
                                {orderInfo.latest_updated_at && (
                                  <span className="ml-1 text-cyan-500">
                                    · {formatOrderDate(orderInfo.latest_updated_at)}
                                  </span>
                                )}
                              </Badge>
                            )}
                          </div>
                        </td>
                        {depotFilter === 'all' ? (
                          // Show all depot columns
                          data.depots.map(depot => (
                            <td key={depot} className="numeric">
                              {item.depots[depot] !== undefined ? (
                                <span className={item.depots[depot] === 0 ? 'text-slate-400' : ''}>
                                  {item.depots[depot].toLocaleString()}
                                </span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          ))
                        ) : (
                          // Show only selected depot column
                          <td className="numeric bg-blue-50 font-medium">
                            {item.depots[depotFilter] !== undefined ? (
                              <span className={item.depots[depotFilter] === 0 ? 'text-slate-400' : 'text-blue-700'}>
                                {item.depots[depotFilter].toLocaleString()}
                              </span>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        )}
                        <td className="numeric font-bold bg-slate-50">{item.total_stock.toLocaleString()}</td>
                        <td className="numeric">{item.buffer_level.toLocaleString()}</td>
                        <td className="numeric text-blue-600">{(item.avg_sale || 0).toLocaleString()}</td>
                        {/* Production columns - conditionally rendered */}
                        {showProductionQty && (() => {
                          const prodInfo = productionData.items?.[item.item_name] || { program_qty: 0, dispatched_qty: 0 };
                          const pendingQty = Math.max(0, prodInfo.program_qty - prodInfo.dispatched_qty);
                          return (
                            <>
                              <td className="numeric bg-blue-50 text-blue-700 font-medium">
                                {prodInfo.program_qty > 0 ? prodInfo.program_qty.toLocaleString() : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="numeric bg-green-50 text-green-700 font-medium">
                                {prodInfo.dispatched_qty > 0 ? prodInfo.dispatched_qty.toLocaleString() : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="numeric bg-orange-50 text-orange-700 font-medium">
                                {pendingQty > 0 ? pendingQty.toLocaleString() : <span className="text-slate-300">—</span>}
                              </td>
                            </>
                          );
                        })()}
                        <td>
                          {item.reorder_required ? (
                            <Badge variant="destructive" className="bg-red-100 text-red-700 border-0">
                              Reorder
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 border-0">
                              OK
                            </Badge>
                          )}
                        </td>
                        <td className="numeric">
                          {item.reorder_required && item.reorder_qty > 0 ? (
                            <span className="font-bold text-red-600">{item.reorder_qty.toLocaleString()}</span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        {!isDepot && (
                          <td className="text-center">
                            {hasActiveOrder ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-cyan-600 border-cyan-200 hover:bg-cyan-50"
                                onClick={() => openContributeDialog(item)}
                                data-testid={`add-to-order-btn-${item.item_name}`}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add Qty
                              </Button>
                            ) : item.reorder_required ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => openCreateOrder(item)}
                                data-testid={`create-order-btn-${item.item_name}`}
                              >
                                <ShoppingCart className="w-3 h-3 mr-1" />
                                Reorder
                              </Button>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                {/* Footer with totals */}
                <tfoot>
                  <tr className="bg-slate-100 font-bold">
                    <td className="sticky left-0 bg-slate-100 z-10">TOTAL</td>
                    <td className="sticky left-[120px] bg-slate-100 z-10">{filteredItems.length} items</td>
                    {depotFilter === 'all' ? (
                      data.depots.map(depot => (
                        <td key={depot} className="numeric">
                          {filteredItems.reduce((sum, item) => sum + (item.depots[depot] || 0), 0).toLocaleString()}
                        </td>
                      ))
                    ) : (
                      <td className="numeric bg-blue-100 text-blue-700">
                        {filteredItems.reduce((sum, item) => sum + (item.depots[depotFilter] || 0), 0).toLocaleString()}
                      </td>
                    )}
                    <td className="numeric bg-slate-200">{totalStockAcrossAll.toLocaleString()}</td>
                    <td className="numeric">—</td>
                    <td className="numeric">—</td>
                    {/* Production totals in footer */}
                    {showProductionQty && (() => {
                      const totalProgram = filteredItems.reduce((sum, item) => {
                        const prodInfo = productionData.items?.[item.item_name] || { program_qty: 0 };
                        return sum + prodInfo.program_qty;
                      }, 0);
                      const totalDispatched = filteredItems.reduce((sum, item) => {
                        const prodInfo = productionData.items?.[item.item_name] || { dispatched_qty: 0 };
                        return sum + prodInfo.dispatched_qty;
                      }, 0);
                      const totalPending = Math.max(0, totalProgram - totalDispatched);
                      return (
                        <>
                          <td className="numeric bg-blue-100 text-blue-700 font-bold">{totalProgram.toLocaleString()}</td>
                          <td className="numeric bg-green-100 text-green-700 font-bold">{totalDispatched.toLocaleString()}</td>
                          <td className="numeric bg-orange-100 text-orange-700 font-bold">{totalPending.toLocaleString()}</td>
                        </>
                      );
                    })()}
                    <td>—</td>
                    <td className="numeric text-red-600">
                      {filteredItems.reduce((sum, item) => sum + (item.reorder_qty || 0), 0).toLocaleString()}
                    </td>
                    {!isDepot && <td>—</td>}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Order Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-red-600" />
              Create Reorder
            </DialogTitle>
            <DialogDescription>
              Create a new reorder request for this item
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedItem && (
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="font-semibold text-lg">{selectedItem.item_name}</p>
                <p className="text-sm text-slate-500">SKU: {selectedItem.sku}</p>
                <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                  <div>
                    <p className="text-slate-400">Current Stock</p>
                    <p className="font-medium">{selectedItem.total_stock.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Buffer Level</p>
                    <p className="font-medium">{selectedItem.buffer_level.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Suggested Qty</p>
                    <p className="font-medium text-red-600">{(selectedItem.reorder_qty || 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Order Quantity *</label>
              <Input
                type="number"
                placeholder="Enter quantity to order"
                value={orderQuantity}
                onChange={(e) => setOrderQuantity(e.target.value)}
                min="1"
                data-testid="order-quantity-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Notes (optional)</label>
              <Input
                placeholder="Add notes for factory..."
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                data-testid="order-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowOrderDialog(false); resetOrderForm(); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateOrder} 
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="submit-order-btn"
            >
              {submitting ? 'Creating...' : 'Create Reorder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contribute to Order Dialog */}
      <Dialog open={showContributeDialog} onOpenChange={setShowContributeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-cyan-600" />
              Add Quantity to Existing Order
            </DialogTitle>
            <DialogDescription>
              Add your depot's quantity requirement to the existing order
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedItem && (
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <div className="flex items-center gap-2 mb-2">
                  <Truck className="w-4 h-4 text-cyan-600" />
                  <p className="font-semibold">{selectedItem.item_name}</p>
                </div>
                <p className="text-sm text-slate-500">SKU: {selectedItem.sku}</p>
                {activeOrders[selectedItem.item_name] && (
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-cyan-600" />
                    <span>Current order total: <strong>{activeOrders[selectedItem.item_name].total_ordered_qty.toLocaleString()}</strong> units</span>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Additional Quantity *</label>
              <Input
                type="number"
                placeholder="Enter quantity to add"
                value={orderQuantity}
                onChange={(e) => setOrderQuantity(e.target.value)}
                min="1"
                data-testid="contribute-quantity-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Notes (optional)</label>
              <Input
                placeholder="Add notes..."
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                data-testid="contribute-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowContributeDialog(false); resetOrderForm(); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleContribute} 
              disabled={submitting}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="submit-contribute-btn"
            >
              {submitting ? 'Adding...' : 'Add to Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryHQ;
